import React, { type ReactElement, useCallback, useLayoutEffect, useState, useMemo } from 'react';
import type { TablePaginationConfig, TableColumnProps, TableProps as AntdTableProps } from 'antd';
import { Table as AntdTable } from 'antd';
import { css } from '@emotion/react';
import { TableEmptyState } from 'components/common/table/table-empty-state';
import { FormattedMessage, useIntl } from 'react-intl';
import Loader from 'components/common/loader';
import styled from 'types/theme.types';
import SearchBar from 'components/common/search-bar';
import { INDENT_BAR_HEIGHT } from 'components/common/table-expand-icon';
import { Resizable } from 'react-resizable';
import { Container } from 'components/common/form-components';
import ActionButton from 'components/common/action-button';
import isString from 'lodash/isString';
import { tableGroupBy } from 'utils/table-group-by.utils';
import type { GroupBySettings, GroupedRowType } from 'utils/table-group-by.utils';
import { typeStyle } from 'constants/typography';

export type ColumnProps = TableColumnProps<any>;
type EmptyMessage = {
  emptyComponent?: ReactElement;
  emptyMessageId?: string;
  // The call to action for a table that is empty because nothing has been added yet.
  emptyAction?: ReactElement;
};

export interface FilterableType {
  defaultQuery?: string;
  onFilter: (query: string) => void;
  error?: null | string;
  placeholder?: string;
  disabled?: boolean;
  showExtendedSearchSyntax?: boolean;
}

export type TableProps<T = any> = AntdTableProps<T> &
  EmptyMessage & {
    paginated?: boolean;
    'data-e2e'?: string;
    filterable?: FilterableType;
    showMore?: ShowMoreOptions;
    isLoading?: boolean;
    renderActions?: ReactElement | null;
    groupBySettings?: GroupBySettings<T>;
  };

export type ShowMoreOptions = {
  hasNextPage?: boolean;
  fetchNextPage: () => Promise<unknown>;
};

/** A control inside a row that acts on itself (expand chevron, tag editor) opts out of the row-hover
 *  highlight by carrying this class — see the tbody rules below. */
export const ROW_HOVER_OFF_CLASS = 'row-hover-off';

export const defaultPagination: TablePaginationConfig = {
  position: ['bottomCenter'],
  defaultPageSize: 10,
  hideOnSinglePage: true,
  showSizeChanger: false
};

/**
 * antd matches these sentinels by reference, so call sites need the real objects, not copies.
 */
export const { EXPAND_COLUMN, SELECTION_COLUMN, SELECTION_ALL, SELECTION_INVERT, SELECTION_NONE } = AntdTable;

export const AntDPrebuiltColumns = new Set([
  AntdTable.EXPAND_COLUMN,
  AntdTable.SELECTION_ALL,
  AntdTable.SELECTION_COLUMN,
  AntdTable.SELECTION_INVERT,
  AntdTable.SELECTION_NONE
]);

const ResizableTitle: React.FunctionComponent<TableProps> = ({ onResize, width, columnKey, ...restProps }: any) => {
  // Was copied from https://ant.design/components/table/#components-table-demo-resizable-column
  // For resizeable columns
  if (!width || Number.isNaN(+width)) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={Number(width)}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          data-e2e={`resize-handle-${columnKey}`}
          onClick={e => {
            e.stopPropagation();
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}>
      <th {...restProps} />
    </Resizable>
  );
};

const components = { header: { cell: ResizableTitle } };

// Resized widths are keyed by something stable across re-renders, because consumers routinely rebuild their
// `columns` array inline on every render.
const getColumnKey = (column: { key?: React.Key; dataIndex?: unknown }, index: number): string => {
  const { key, dataIndex } = column;
  const fromDataIndex = Array.isArray(dataIndex) ? dataIndex.join('.') : (dataIndex as React.Key | undefined);
  return String(key ?? fromDataIndex ?? index);
};

const Table: React.FunctionComponent<TableProps> = props => {
  const {
    paginated,
    columns,
    emptyMessageId,
    emptyComponent,
    emptyAction,
    'data-e2e': dataE2e,
    filterable,
    showMore = null,
    isLoading,
    renderActions = null,
    groupBySettings = null,
    dataSource,
    ...passedProps
  } = props;

  const [displayedColumns, setDisplayedColumns] = useState(columns || []);
  // Kept separately from displayedColumns so a user's resize survives the columns prop being rebuilt.
  const [resizedWidths, setResizedWidths] = useState<Record<string, number>>({});

  const handleResize = useCallback<any>(
    (columnKey: string) =>
      (e: any, { size: { width } }: any) => {
        setResizedWidths(widths => ({ ...widths, [columnKey]: width }));
      },
    []
  );

  const intl = useIntl();

  useLayoutEffect(() => {
    if (columns) {
      setDisplayedColumns(
        columns.map((column, index) => {
          if (AntDPrebuiltColumns.has(column)) {
            return column;
          }

          const columnKey = getColumnKey(column, index);
          const fixedColumn = { ...column };

          // only for string titles we should fetch translation. for react components we should not
          if (isString(column.title)) {
            fixedColumn.title = intl.formatMessage({ id: column.title as string });
          }
          if (columnKey in resizedWidths) {
            fixedColumn.width = resizedWidths[columnKey];
          }
          fixedColumn.onHeaderCell = (column: any) => ({
            width: column.width,
            columnKey,
            onResize: handleResize(columnKey)
          });

          return fixedColumn;
        })
      );
    }
  }, [columns, handleResize, intl, resizedWidths]);

  const groupedItems: GroupedRowType<any>[] | undefined = useMemo(() => {
    if (groupBySettings && columns && dataSource) {
      return tableGroupBy(columns, Array.from(dataSource), groupBySettings);
    }
  }, [groupBySettings, columns, dataSource]);

  let rowClassName;

  // The Figma composition carries its own title, so the call site's `emptyMessageId` becomes the
  // description. `no-data` is the only type wired here: this component never sees the search query
  // (`filterable.onFilter` is a parent callback), so call sites pick `no-results` themselves via
  // `emptyComponent` (APO-466).
  const emptyText = emptyMessageId ? (
    <div data-e2e={`${dataE2e}-empty`}>
      <TableEmptyState type="no-data" description={<FormattedMessage id={emptyMessageId} />} action={emptyAction} />
    </div>
  ) : null;

  const locale = emptyComponent ? { emptyText: emptyComponent } : emptyText ? { emptyText } : {};

  if (passedProps.loading) {
    return <Loader />;
  }

  if (passedProps.onRow) {
    rowClassName = () => 'clickable';
  }

  return (
    <TableContainer data-e2e={dataE2e}>
      {filterable || renderActions ? (
        <ControlPanelContainer>
          {filterable ? (
            <SearchBarContainer>
              <SearchBar
                defaultValue={filterable.defaultQuery}
                disabled={filterable.disabled}
                onFilter={filterable.onFilter}
                placeholder={filterable.placeholder || 'table.filter.default'}
                error={filterable.error}
                showExtendedSearchSyntax={filterable.showExtendedSearchSyntax}
              />
            </SearchBarContainer>
          ) : null}
          {renderActions ? <div>{renderActions}</div> : null}
        </ControlPanelContainer>
      ) : null}
      <ResizeableTable
        loading={isLoading}
        data-e2e="table-component"
        columns={displayedColumns}
        components={{ ...components, ...passedProps.components }}
        rowKey={passedProps.rowKey as any}
        dataSource={groupedItems ?? dataSource}
        {...{ rowClassName, locale: isLoading ? { emptyText: <EmptyMessagePlaceHolder /> } : locale, ...passedProps }}
        pagination={paginated ? { ...defaultPagination, ...passedProps.pagination } : passedProps.pagination || false}
      />
      {showMore?.hasNextPage ? (
        <StyledCenteredContainer>
          <ActionButton action={showMore.fetchNextPage} data-e2e="show-more-button">
            <FormattedMessage id="ellipsis.show.more" />
          </ActionButton>
        </StyledCenteredContainer>
      ) : null}
    </TableContainer>
  );
};

const ControlPanelContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;

  ${({ theme }) => css`
    /* Figma's Page title pattern (295:27002) leads the card's control row with the search. */
    flex-direction: row;
    margin-bottom: ${theme.spacing.lg};
  `}
`;

const SearchBarContainer = styled.div`
  /* The projects reference sizes its search to a third of the card (Col md={8}). */
  margin-left: 0;
  margin-right: auto;
  width: 33%;
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTable = styled(AntdTable)`
  overflow: auto;

  /* Doubled throughout: antd declares \`background\` and \`padding\` on these elements, so a single
         class ties on specificity and loses on source order - its cssinjs tag is injected after
         emotion's. Measured, not assumed. \`height\` and the type roles sit at ordinary specificity:
         of those, antd declares only the header weight, which the bridge sets to the same label
         weight. */
  && .ant-table-tbody > tr > td.ant-table-cell-row-hover {
    background: var(--component-table-row-background-hover);
  }

  /* An in-row control that acts on itself — the tree chevron, the tags editor — marks itself with
         ROW_HOVER_OFF_CLASS, and hovering it withdraws the row highlight: the row highlight promises a
         row click, and these controls do something else. Painted back to the resting transparent
         rather than un-matching the rule above, because antd puts its own background on the same
         cells and would show through. */
  && .ant-table-tbody > tr:has(.${ROW_HOVER_OFF_CLASS}:hover) > td.ant-table-cell-row-hover {
    background: transparent;
  }

  /* The indent bar is a float and needs a height to displace nested content, but the row's own
         line-height, not the taller 26px icon box: at 26px the bar overshot the 20px label and pushed it
         ~3px above the cell's centre (APO-729 review). */
  .ant-table-row-indent {
    height: ${INDENT_BAR_HEIGHT}px;
  }

  /* antd puts overflow-wrap: break-word on every cell, and table-layout turns fixed as soon as any
         column declares ellipsis - narrow headers then break MID-word ("Environmen / ts"). Headers are
         labels: one line, clipped, never fragmented. */
  .ant-table-thead > tr > th {
    height: 48px;
    ${typeStyle.labelMd}
    white-space: nowrap;
    word-break: keep-all;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-table-tbody > tr > td {
    height: 48px;
    ${typeStyle.bodyMd}
  }

  /* Zero, not a token: with \`height\` and antd's \`vertical-align: middle\` the row is exactly 48px
         whatever it holds. Any vertical padding adds the tallest cell's line box on top, and a
         baseline-aligned inline-block exceeds its own height - a 22px Tag demands 28px, which rendered
         53px rows. */
  /* An expanded row's cell is also a \`tbody > tr > td\`, and antd's only padding rule for it is on
         \`.ant-table-expanded-row-fixed\` (0,2,0), which exists only with \`scroll.x\` - so it cannot
         restore what the doubled class removes. Excluded, or panel content sits flush against the row. */
  && {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr:not(.ant-table-expanded-row) > td {
      padding-block: 0;
    }
  }

  /* The bridge gives the active pager its border; antd draws the label from the same token. */
  && .ant-pagination-item-active a {
    color: var(--component-table-cell-text-strong);
  }
`;

const ResizeableTable = styled(StyledTable)`
  // Was copied from https://ant.design/components/table/#components-table-demo-resizable-column
  // For resizeable columns
  .react-resizable {
    position: relative;
    background-clip: padding-box;
  }

  .react-resizable-handle {
    position: absolute;
    right: -5px;
    bottom: 0;
    z-index: 1;
    width: 10px;
    height: 100%;
    cursor: col-resize;
  }
`;

const StyledCenteredContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;

  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const EmptyMessagePlaceHolder = styled.div`
  min-height: 150px; //our empty message height, to avoid weird jumps when loading and there is no data
`;

export default Table;
